"use client"

import { addToCart } from "@lib/data/cart"
import { getProductByHandle } from "@lib/data/products"
import { HttpTypes } from "@medusajs/types"
import { Button, Text, clx } from "@modules/common/components/ui"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Modal from "@modules/common/components/modal"
import Spinner from "@modules/common/icons/spinner"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import ProductPrice from "@modules/products/components/product-price"
import { useOptionSelection } from "@modules/products/hooks/use-option-selection"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useEffect, useState } from "react"

type QuickViewModalProps = {
  handle: string
  isOpen: boolean
  close: () => void
}

// Mini PDP in a modal: gallery, price, option pills and add-to-cart,
// fetched client-side so grid cards stay light.
export default function QuickViewModal({
  handle,
  isOpen,
  close,
}: QuickViewModalProps) {
  const countryCode = useParams().countryCode as string
  const [product, setProduct] = useState<HttpTypes.StoreProduct | null>(null)

  useEffect(() => {
    if (!isOpen || product) {
      return
    }

    let cancelled = false

    getProductByHandle(handle, countryCode).then((result) => {
      if (!cancelled) {
        setProduct(result)
      }
    })

    return () => {
      cancelled = true
    }
  }, [isOpen, product, handle, countryCode])

  return (
    <Modal isOpen={isOpen} close={close} size="large" data-testid="quick-view-modal">
      <Modal.Title>Quick View</Modal.Title>
      {!product ? (
        <div
          className="flex items-center justify-center py-16"
          data-testid="quick-view-loading"
        >
          <Spinner size="24" />
        </div>
      ) : (
        <QuickViewContent product={product} countryCode={countryCode} />
      )}
    </Modal>
  )
}

function QuickViewContent({
  product,
  countryCode,
}: {
  product: HttpTypes.StoreProduct
  countryCode: string
}) {
  const [imageIndex, setImageIndex] = useState(0)
  const [isAdding, setIsAdding] = useState(false)
  const [added, setAdded] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const {
    options,
    setOptionValue,
    selectedVariant,
    visibleOptionValues,
    isValidVariant,
    inStock,
  } = useOptionSelection(product)

  const images = product.images?.length
    ? product.images
    : product.thumbnail
    ? [{ id: "thumbnail", url: product.thumbnail }]
    : []
  const mainImage = images[imageIndex] ?? images[0]

  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return

    setAddError(null)
    setIsAdding(true)

    try {
      await addToCart({
        variantId: selectedVariant.id,
        quantity: 1,
        countryCode,
      })
      setAdded(true)
    } catch {
      setAddError("Couldn't add to cart — please try again")
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="flex flex-col small:flex-row gap-6 pt-4 overflow-y-auto">
      <div className="flex flex-col gap-y-3 w-full small:w-1/2 shrink-0">
        <div className="relative aspect-square w-full rounded-md bg-white border border-gray-100 overflow-hidden">
          {mainImage?.url ? (
            <Image
              src={mainImage.url}
              alt={product.title ?? "Product image"}
              className="object-contain object-center p-4"
              sizes="(max-width: 1024px) 90vw, 400px"
              fill
              data-testid="quick-view-main-image"
            />
          ) : (
            <div className="w-full h-full bg-gray-50" />
          )}
        </div>
        {images.length > 1 && (
          <div className="flex gap-x-2 overflow-x-auto">
            {images.map((image, index) => (
              <button
                key={image.id ?? image.url}
                type="button"
                onClick={() => setImageIndex(index)}
                aria-label={`Show image ${index + 1}`}
                aria-current={index === imageIndex}
                className={clx(
                  "relative w-16 h-16 shrink-0 rounded-md border bg-white overflow-hidden",
                  index === imageIndex
                    ? "border-gray-900"
                    : "border-gray-200 hover:border-gray-400"
                )}
                data-testid="quick-view-thumbnail"
              >
                {image.url && (
                  <Image
                    src={image.url}
                    alt=""
                    className="object-contain object-center p-1"
                    sizes="64px"
                    fill
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-y-4 flex-1 min-w-0">
        <Text className="text-large-semi text-ui-fg-base">{product.title}</Text>
        <ProductPrice product={product} variant={selectedVariant} />
        {(product.variants?.length ?? 0) > 1 && (
          <div className="flex flex-col gap-y-4">
            {(product.options || []).map((option) => (
              <div key={option.id}>
                <OptionSelect
                  option={option}
                  current={options[option.id]}
                  updateOption={setOptionValue}
                  title={option.title ?? ""}
                  visibleValues={visibleOptionValues[option.id]}
                  disabled={isAdding}
                  data-testid="quick-view-options"
                />
              </div>
            ))}
          </div>
        )}
        <Button
          onClick={handleAddToCart}
          disabled={!inStock || !selectedVariant || isAdding || !isValidVariant}
          variant="primary"
          className="w-full h-10"
          isLoading={isAdding}
          data-testid="quick-view-add-button"
        >
          {!selectedVariant && (product.variants?.length ?? 0) > 1
            ? "Select options"
            : selectedVariant && !inStock
            ? "Out of stock"
            : added
            ? "Added — add another"
            : "Add to cart"}
        </Button>
        {addError && (
          <Text role="alert" className="text-small-regular text-red-600">
            {addError}
          </Text>
        )}
        <LocalizedClientLink
          href={`/products/${product.handle}`}
          className="text-small-regular underline text-ui-fg-subtle hover:text-ui-fg-base"
          data-testid="quick-view-details-link"
        >
          View full details
        </LocalizedClientLink>
      </div>
    </div>
  )
}
